import { Where } from "@/types/crm/configuration/question_conditionals";
import { QuestionWithConditionResult, QuestionWithDependents } from "../useGetQuestionsBySurveyId.types";

const LOG = false;

export function groupQuestionsByDependencies(
    questions: Array<QuestionWithConditionResult>
): Array<QuestionWithDependents> {
    LOG && console.group("Grouping questions by dependencies");

    LOG && console.log("Input questions:", questions.map(q => q.id));

    // Step 1: Initialize all questions with empty dependent_questions
    const allQuestions: Array<QuestionWithDependents> = questions.map(question => ({
        ...question,
        dependent_questions: []
    }));

    const questionMap = new Map(allQuestions.map(q => [q.id, q]));

    // Step 2: Build dependency relationships and identify root questions
    const rootQuestions: Array<QuestionWithDependents> = [];
    const dependentQuestionIds = new Set<string>();

    LOG && console.log("\n=== Analyzing dependencies ===");

    for (const question of allQuestions) {
        const dependencies = extractAllDependencies(question.selected_configuration?.conditionals?.where);

        if (dependencies.length > 0) {
            LOG && console.log(`${question.id} depends on:`, dependencies);
            dependentQuestionIds.add(question.id);

            // Add this question to each of its dependencies' dependent_questions
            for (const dependencyId of dependencies) {
                const parentQuestion = questionMap.get(dependencyId);
                if (parentQuestion) {
                    addDependentQuestion(parentQuestion, question);
                    LOG && console.log(`Added ${question.id} to ${dependencyId}'s dependents`);
                } else {
                    LOG && console.warn(`Dependency ${dependencyId} not found for question ${question.id}`);
                }
            }
        } else {
            LOG && console.log(`${question.id} is independent`);
        }
    }

    // Step 3: Identify root questions (questions that are not dependent on others)
    for (const question of allQuestions) {
        if (!dependentQuestionIds.has(question.id)) {
            rootQuestions.push(question);
            LOG && console.log(`${question.id} is a root question`);
        }
    }

    LOG && console.log("\n=== Final hierarchy ===");
    LOG && logHierarchy(rootQuestions, 0);

    LOG && console.groupEnd();
    return rootQuestions;
}

function addDependentQuestion(parent: QuestionWithDependents, dependent: QuestionWithDependents) {
    // Check if already exists to avoid duplicates
    const alreadyExists = parent.dependent_questions.some(existing => existing.id === dependent.id);
    if (!alreadyExists) {
        parent.dependent_questions.push(dependent);
    }
}

function logHierarchy(questions: Array<QuestionWithDependents>, depth: number) {
    const indent = '  '.repeat(depth);

    for (const question of questions) {
        console.log(`${indent}${question.id} - ${question.question || 'No title'}`);
        if (question.dependent_questions.length > 0) {
            console.log(`${indent}  Dependencies:`);
            logHierarchy(question.dependent_questions, depth + 2);
        }
    }
}

export function extractAllDependencies(where?: Where): string[] {
    if (!where) return [];

    const dependencies = new Set<string>();

    function extractFromWhere(w: Where) {
        for (const [key, value] of Object.entries(w)) {
            if (key === 'and' || key === 'or') {
                // Recursively extract from nested conditions
                if (Array.isArray(value)) {
                    value.forEach(nestedWhere => extractFromWhere(nestedWhere));
                }
            } else {
                // key is a question ID
                dependencies.add(key);

                // If value is an array of Where objects, extract from those too
                if (Array.isArray(value)) {
                    value.forEach(nestedWhere => extractFromWhere(nestedWhere));
                }
            }
        }
    }

    extractFromWhere(where);
    return Array.from(dependencies);
}

export function createDependencyGroups(
    questions: Array<QuestionWithConditionResult>
): Map<string, Array<QuestionWithConditionResult>> {
    console.group("Creating dependency groups");

    const groups = new Map<string, Array<QuestionWithConditionResult>>();
    const questionMap = new Map(questions.map(q => [q.id, q]));

    // Initialize empty groups for all questions
    for (const question of questions) {
        groups.set(question.id, []);
    }

    // Build direct dependency relationships
    for (const question of questions) {
        const dependencies = extractAllDependencies(question.selected_configuration?.conditionals?.where);

        for (const dependencyId of dependencies) {
            if (questionMap.has(dependencyId)) {
                const existingGroup = groups.get(dependencyId) || [];
                existingGroup.push(question);
                groups.set(dependencyId, existingGroup);
                console.log(`Added ${question.id} to group of ${dependencyId}`);
            }
        }
    }

    // Expand to include indirect dependencies
    let expanded = true;
    let iterations = 0;
    const maxIterations = questions.length;

    while (expanded && iterations < maxIterations) {
        expanded = false;
        iterations++;

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        for (const [questionId, directDependents] of groups.entries()) {
            const currentDependents = [...directDependents];

            for (const dependant of currentDependents) {
                const nestedDependents = groups.get(dependant.id) || [];

                for (const nestedDependant of nestedDependents) {
                    const alreadyExists = directDependents.some(existing => existing.id === nestedDependant.id);

                    if (!alreadyExists) {
                        directDependents.push(nestedDependant);
                        expanded = true;
                    }
                }
            }
        }
    }

    console.log("Final groups:", Array.from(groups.entries()).map(([key, deps]) => ({
        questionId: key,
        dependents: deps.map(d => d.id)
    })));

    console.groupEnd();
    return groups;
}
